/**
 * Schedule v1 ↔ v2 compat layer
 *
 * Pure, total conversions between the flat v1 schedule format and the keyed v2
 * representation. Each sub-domain (metadata, features, rules, patterns) has
 * its own mapper; {@link ScheduleV2Compat.fromV1} and {@link ScheduleV2Compat.toV1}
 * compose them.
 *
 * ## fromV1
 *
 * Structural reshape — no semantic interpretation:
 * - Rule `id` → record key (bijection, since v1 enforces unique ids)
 * - Array index → `priority` (preserving the v1 "last wins" evaluation order)
 * - Pattern data lifted out of its `[kind]_pattern` envelope into `pattern`
 *
 * ## toV1
 *
 * Reverse reshape — flattens keyed/array rule slots back to a flat array:
 * - Record key → rule `id` (single rules); `"${key}:${index}"` for array slots
 * - Rules sorted by `priority` ascending (stable); unprioritized rules last
 * - Pattern data re-wrapped into the `[kind]_pattern` envelope
 * - `suggested_input_time_precision_in_minutes` hardcoded to `10` (the only
 *   value v1 accepts)
 */
import { match, P } from "ts-pattern";
import { ScheduleSchema } from "./schedule";
import type {
  Schedule as ScheduleV1,
  ScheduleMetadata as ScheduleV1Metadata,
  Rule as ScheduleV1Rule,
} from "./schedule";
import type { ScheduleV2, ScheduleV2Metadata, ScheduleV2Rule } from "./schedule2";

// #region fromV1

const mapMetadata = (v1: ScheduleV1Metadata): ScheduleV2Metadata => ({
  updated_at: new Date(v1.updated_at_utc),
  precision: v1.expected_time_precision_in_minutes,
  timezone: v1.timezone,
});

const mapFeatures = (v1: ScheduleV1["features"]): ScheduleV2["features"] =>
  Object.fromEntries(
    Object.entries(v1).map(([name, feature]) => [
      name,
      { options: feature.allowed_states, default: feature.default_state },
    ]),
  );

const extractPattern = (rule: ScheduleV1Rule): Record<string, unknown> =>
  match(rule)
    .with({ kind: "weekly_pattern" }, (r) => ({
      kind: r.kind,
      ...r.weekly_pattern,
    }))
    .with({ kind: "rotating_pattern" }, (r) => ({
      kind: r.kind,
      ...r.rotating_pattern,
    }))
    .with({ kind: "absolute_range" }, (r) => ({
      kind: r.kind,
      ...r.absolute_range,
    }))
    .exhaustive();

const mapRule = (rule: ScheduleV1Rule, index: number): [string, ScheduleV2["rules"][string]] => [
  rule.id,
  {
    name: rule.name,
    pattern: extractPattern(rule),
    action: rule.features,
    priority: index,
  },
];

const fromV1 = (schedule: ScheduleV1): ScheduleV2 => ({
  schema_version: "2",
  metadata: mapMetadata(schedule.metadata),
  features: mapFeatures(schedule.features),
  rules: Object.fromEntries(schedule.rules.map((rule, i) => mapRule(rule, i))),
});

// #endregion

// #region toV1

const unmapMetadata = (v2: ScheduleV2Metadata): ScheduleV1Metadata => ({
  updated_at_utc: v2.updated_at.toISOString(),
  suggested_input_time_precision_in_minutes: 10 as const,
  expected_time_precision_in_minutes: v2.precision,
  timezone: v2.timezone,
});

const unmapFeatures = (v2: ScheduleV2["features"]) =>
  Object.fromEntries(
    Object.entries(v2).map(([name, feature]) => [
      name,
      { default_state: feature.default, allowed_states: [...feature.options] },
    ]),
  );

type V2RuleEntry = ScheduleV2Rule;

const flattenRules = (v2Rules: ScheduleV2["rules"]): Array<{ id: string; entry: V2RuleEntry }> => {
  const flat: Array<{ id: string; entry: V2RuleEntry }> = [];
  for (const [key, slot] of Object.entries(v2Rules)) {
    if (slot === undefined) continue;
    if ("pattern" in slot) {
      flat.push({ id: key, entry: slot });
    } else {
      for (const [i, element] of slot.entries()) {
        flat.push({ id: `${key}:${String(i)}`, entry: element });
      }
    }
  }
  return flat;
};

const restorePattern = (pattern: Record<string, unknown>) => {
  const { kind, ...rest } = pattern;
  return match(kind)
    .with("weekly_pattern", () => ({ kind: "weekly_pattern" as const, weekly_pattern: rest }))
    .with("rotating_pattern", () => ({ kind: "rotating_pattern" as const, rotating_pattern: rest }))
    .with("absolute_range", () => ({ kind: "absolute_range" as const, absolute_range: rest }))
    .with(P._, (k) => {
      throw new Error(`unknown pattern kind: ${String(k)}`);
    })
    .exhaustive();
};

const unmapRule = (id: string, entry: V2RuleEntry) => ({
  id,
  name: entry.name,
  features: entry.action,
  ...restorePattern(entry.pattern),
});

const toV1 = (schedule: ScheduleV2): ScheduleV1 => {
  const flat = flattenRules(schedule.rules);
  flat.sort((a, b) => (a.entry.priority ?? Infinity) - (b.entry.priority ?? Infinity));

  return ScheduleSchema.parse({
    schema_version: "1",
    metadata: unmapMetadata(schedule.metadata),
    features: unmapFeatures(schedule.features),
    rules: flat.map(({ id, entry }) => unmapRule(id, entry)),
  });
};

// #endregion

export const ScheduleV2Compat = { fromV1, toV1 } as const;
